import { compileSchema, draft07 } from 'json-schema-library';

import {
  JSON_SCHEMA_DRAFT_07,
  JSON_SCHEMA_VERSION_DRAFT_07,
} from '@/constants/jsonSchema';

export interface ConvertOptions {
  /**
   * Schema version to use. Must be compatible with backend validator.
   * Frontend (AJV 2019) supports: draft-07, draft-2019-09
   *
   * Using draft-07 for maximum compatibility.
   */
  schemaVersion?: typeof JSON_SCHEMA_VERSION_DRAFT_07;

  /**
   * Title for the generated schema
   */
  title?: string;
}

/**
 * Converts JSON data to JSON Schema using json-schema-library
 *
 * This function generates a JSON Schema from example JSON data.
 * It uses draft-07 by default for compatibility with both frontend and backend validators.
 *
 * Backend validator (gojsonschema): supports draft-04, draft-06, draft-07
 * Frontend validator (AJV 2019): supports draft-07, draft-2019-09
 *
 * @param jsonString - The JSON data to convert to schema
 * @param options - Configuration options
 * @returns Promise<string> - The generated JSON Schema as a string
 *
 * @example
 * ```typescript
 * const json = '{"name": "John", "age": 30}';
 * const schema = await convertJsonToSchema(json);
 * // Result: JSON Schema with draft-07
 * ```
 */
export async function convertJsonToSchema(
  jsonString: string,
  options: ConvertOptions = {},
): Promise<string> {
  try {
    // Parse and validate JSON input
    const jsonData = JSON.parse(jsonString);

    const { title } = options;

    // Create a schema node using draft-07
    const schemaNode = compileSchema(
      { $schema: JSON_SCHEMA_DRAFT_07 },
      { drafts: [draft07] },
    );

    // Generate schema from the data using json-schema-library
    const schema = schemaNode.createSchema(jsonData);

    // Recursively add required fields for all object properties
    const addRequiredFields = (schemaObj: any, dataObj: any) => {
      if (
        schemaObj.type === 'object' &&
        schemaObj.properties &&
        typeof dataObj === 'object' &&
        dataObj !== null
      ) {
        const required = Object.keys(schemaObj.properties).filter(
          (key) => dataObj[key] !== null && dataObj[key] !== undefined,
        );
        if (required.length > 0) {
          schemaObj.required = required;
        }

        // Recursively process nested objects
        Object.keys(schemaObj.properties).forEach((key) => {
          if (schemaObj.properties[key].type === 'object' && dataObj[key]) {
            addRequiredFields(schemaObj.properties[key], dataObj[key]);
          }
        });
      }
    };

    addRequiredFields(schema, jsonData);

    // Add $schema version and title
    const finalSchema = {
      $schema: JSON_SCHEMA_DRAFT_07,
      ...schema,
      ...(title && { title }),
    };

    return JSON.stringify(finalSchema, null, 2);
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new Error('Invalid JSON: ' + error.message);
    }
    throw new Error('Failed to generate schema: ' + error.message);
  }
}
